import type { AccessibleOrg } from '../types/org-selection.types';
import { getScopeRole } from '@/services/current-context';
import { getEntityScope } from './entity-scope-read';
import { getStore } from '../store/get-store';
import { scopeCleared } from '../slice/entityScopeSlice';
import { ROUTES } from '../config/routes';
import { isInternalAdmin, isInternalRole } from '../util/roles';

/**
 * True only for a row that has actually been through the DV-3012
 * normalisation (`transformResponse` on the two `OrgData`-feeding endpoints).
 * Defends against a stale-shape payload a session already had in
 * `localStorage.OrgData` when that normalisation shipped: pre-DV-3012 rows
 * carried `company`/`entityName` and never `name`, so a session that has not
 * logged back in since deploy would otherwise have `name: undefined` on
 * every row. That is not merely a cosmetic miss — a lookup that matches a
 * row by `entityId` (which is identical in both shapes) still finds the row,
 * then reads an `undefined` name and treats it as an unresolvable match
 * rather than a cache miss, so code that only refetches on "no match found"
 * never refetches. Remove once no live session predates the normalisation
 * deploy.
 *
 * Applied PER ROW, not to the payload as a whole. A whole-payload `every()`
 * test conflates the two cases it has to tell apart: a pre-normalisation
 * payload has no named row at all and still reads as a cache miss under
 * per-row filtering, whereas ONE unusable row in an otherwise current
 * payload used to blank the entire list. `normalizeCustomerOrgList` takes
 * `name` from user-service's `company`, which is nullable on the wire, so a
 * single account with a blank company would turn `hasMultipleOrgs()` false —
 * which hides the `/error` page's "Switch Organization" CTA (BC-2's escape
 * hatch, `unauthorized.tsx`) and `PropertyNotFound`'s, the only two surfaces
 * that gate on it — empty `getOrgSelectedAllData()`, and make the resolution
 * cache a permanent miss. A row with no display name cannot
 * be shown or picked either way; dropping just that row is the difference
 * between losing one account and losing the session's whole org list.
 *
 * `resolveEntityScope` (in `entity-scope-resolution.ts`) now reads the cached
 * list through `getAllOrganisation()` below rather than re-parsing `OrgData`
 * itself, so the predicate has one caller-visible home. This export remains
 * for tests and for any future direct reader.
 */
export const isNormalisedRow = (item: unknown): item is AccessibleOrg =>
  typeof item === 'object' &&
  item !== null &&
  'name' in item &&
  typeof item.name === 'string' &&
  item.name.length > 0;

/**
 * The usable rows of `OrgData` — empty when it is absent, corrupt, or still
 * in the pre-normalisation shape (in which no row is usable).
 *
 * Exported since DV-3012 so `OrganizationSelectionPage` can ask "do I have a
 * usable list?" without a fourth hand-rolled parse-and-validate. An empty
 * answer is that page's cue to refetch — see its mount effect.
 */
export const getAllOrganisation = (): AccessibleOrg[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(localStorage.getItem('OrgData') || '[]');
  } catch {
    // A corrupt payload must not throw on a render path.
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isNormalisedRow);
};

/**
 * True when the active user has access to more than one organization.
 * Mirrors the gate `AppHeader`'s switcher dropdown uses (`length > 1`), so
 * any UI that offers a "Switch organization" affordance can hide it for
 * single-org users (typical CUSTOMER_USER pattern). Both internal roles get
 * the full internal entity list; customer users get their assigned orgs only —
 * both populate the same `OrgData` key at login.
 */
export const hasMultipleOrgs = (): boolean => {
  return getAllOrganisation().length > 1;
};

/**
 * Where a user with no account chosen belongs:
 *  - INTERNAL_ADMIN → the accounts console, which lists every account;
 *  - INTERNAL_POST_PROCESSING → Global Orders (DV-3337). Their work is
 *    cross-account, they enter an account's photos through its Manage Assets
 *    action, and the accounts console's endpoint is INTERNAL_ADMIN-only;
 *  - everyone else → the customer org picker.
 *
 * Shared with `resolveEntityScope`, which sends a user with no usable scope to
 * the same place the "Switch Organization" affordances do. One branch, so the
 * bootstrap and the switch affordances cannot disagree about where a user
 * without a scope belongs — which is also why those affordances take their
 * label from `orgSwitchLabel()` below rather than hardcoding it.
 */
export const unscopedLandingRoute = (): string => {
  const role = getScopeRole();
  if (isInternalAdmin(role)) return ROUTES.ACCOUNTS;
  if (isInternalRole(role)) return ROUTES.GLOBAL_ORDERS;
  return '/select-organization';
};

/**
 * Whether `initiateOrgSwitch()` takes this user back to Global Orders rather
 * than to an account chooser — true for a post-processing user. Copy that
 * describes a switch control reads this, so its words follow the destination.
 */
export const orgSwitchReturnsToGlobalOrders = (): boolean =>
  unscopedLandingRoute() === ROUTES.GLOBAL_ORDERS;

/**
 * The label for a control that runs `initiateOrgSwitch()`. `chooserLabel` is
 * the surface's own wording for opening an account chooser, and it stands
 * wherever `unscopedLandingRoute()` is one. A post-processing user's is not —
 * the control takes them back to Global Orders — so it says that instead.
 */
export const orgSwitchLabel = (chooserLabel: string): string =>
  orgSwitchReturnsToGlobalOrders() ? 'Back to Global Orders' : chooserLabel;

/**
 * Clear the current-org localStorage state and return the route the caller
 * should navigate to for picking a new org.
 *
 * Used by every "Switch organization" affordance in the app so the routing
 * + cleanup stays in lockstep with `AppHeader`'s switcher dropdown. The
 * clearing is what separates this from `unscopedLandingRoute()` alone.
 */
export const initiateOrgSwitch = (): string => {
  // Dispatched, not deleted directly (DV-3013, PR #1146 review finding 2).
  // `scopeCleared` drops the name and the tree and leaves the entity id — the
  // exact keys this used to remove — and the persistence middleware performs
  // the removal as a consequence, so the observable localStorage effect is
  // unchanged. Doing it by hand left the slice still holding the old org,
  // and because persistence rewrites the whole key set from the slice, one
  // later dispatch wrote `selectedOrg` back. Four of this function's call
  // sites navigate client-side rather than reloading, so nothing else would
  // have corrected it.
  getStore().dispatch(scopeCleared());
  return unscopedLandingRoute();
};

export const getOrgSelectedAllData = (): AccessibleOrg[] => {
  const selected = getEntityScope().entityName;
  if (!selected) return [];
  // BC-9: this used to filter on `company`, which admin-flow rows never
  // carried, so it returned [] for every internal admin.
  return getAllOrganisation().filter((org) => org.name === selected);
};

export const updateOrgData = (entityId: string | null, newName: string) => {
  const orgData = getAllOrganisation();
  if (orgData.length === 0) return;
  // One shape ⇒ no role branch. This used to write `entityName` for
  // INTERNAL_ADMIN and `company` for everyone else.
  for (const item of orgData) {
    if (item.entityId === entityId) item.name = newName;
  }
  // Writes back the FILTERED list, so any unusable row is dropped from
  // storage here. That is the same row every reader already skips, and the
  // next login refetches the list wholesale.
  localStorage.setItem('OrgData', JSON.stringify(orgData));
};
